/**
 * Shown after WorkOS login when the user belongs to multiple organizations
 * and WorkOS did not return a single organization_id.
 */

import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

const AGENT_BASE = process.env.REACT_APP_AGENT_URL || 'http://localhost:8090';

interface OrgOption {
  id: string;
  name: string;
}

export function OrgSelectPage() {
  const [params] = useSearchParams();
  const sid = params.get('sid') || '';
  const [email, setEmail] = useState('');
  const [orgs, setOrgs] = useState<OrgOption[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState<string | null>(null);

  useEffect(() => {
    if (!sid) {
      setError('Missing session. Return to login and try again.');
      setLoading(false);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(
          `${AGENT_BASE}/auth/pending-org-data?sid=${encodeURIComponent(sid)}`,
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (!res.ok) {
          setError(
            typeof data.error === 'string'
              ? data.error
              : 'Could not load organizations. Start sign-in again.',
          );
          setLoading(false);
          return;
        }
        setEmail(data.email || '');
        setOrgs(Array.isArray(data.orgs) ? data.orgs : []);
        setLoading(false);
      } catch {
        if (!cancelled) {
          setError('Network error loading organizations.');
          setLoading(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [sid]);

  const choose = async (organizationId: string) => {
    setSubmitting(organizationId);
    setError(null);
    try {
      const res = await fetch(`${AGENT_BASE}/auth/complete-org-selection`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ sid, organization_id: organizationId }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setError(
          typeof data.error === 'string' ? data.error : 'Could not complete sign-in.',
        );
        setSubmitting(null);
        return;
      }
      if (data.redirect_url && typeof data.redirect_url === 'string') {
        window.location.assign(data.redirect_url);
        return;
      }
      setError('Unexpected response from server.');
    } catch {
      setError('Network error completing sign-in.');
    }
    setSubmitting(null);
  };

  if (loading) {
    return (
      <div className="h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-600">Loading organizations…</div>
      </div>
    );
  }

  return (
    <div className="h-screen bg-gray-50 flex items-center justify-center p-4">
      <div className="w-full max-w-md bg-white rounded-lg shadow-sm border border-gray-200 p-8">
        <h1 className="text-xl font-semibold text-gray-900 text-center mb-1">
          Select organization
        </h1>
        <p className="text-sm text-gray-500 text-center mb-6">
          CXS Agents
          {email ? (
            <span className="block mt-1 text-gray-600">{email}</span>
          ) : null}
        </p>
        {error && (
          <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm text-red-700">
            {error}
          </div>
        )}
        <div className="space-y-2">
          {orgs.map((o) => (
            <button
              key={o.id}
              type="button"
              disabled={!!submitting}
              onClick={() => choose(o.id)}
              className="w-full text-left px-4 py-3 rounded-md border border-gray-200 hover:bg-gray-50 text-sm font-medium text-gray-800 disabled:opacity-50"
            >
              {submitting === o.id ? 'Signing in…' : o.name || o.id}
            </button>
          ))}
        </div>
        {orgs.length === 0 && !error && (
          <p className="text-sm text-gray-500 text-center">No organizations available.</p>
        )}
        <button
          type="button"
          className="mt-6 w-full text-sm text-blue-600 hover:underline"
          onClick={() => {
            window.location.href = '/login';
          }}
        >
          Back to login
        </button>
      </div>
    </div>
  );
}
